import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import { forwardRef } from "react";

import {
  NAVIGATION_MENU_CONTENT_LIST_CLASS,
  type TNavigationMenuElement,
  type TNavigationMenuProps,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenu.config";
import {
  NavigationMenuContent,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuContent";
import {
  NavigationMenuItem,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuItem";
import {
  NavigationMenuLink,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuLink";
import {
  NavigationMenuList,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuList";
import {
  NavigationMenuSeparator,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuSeparator";
import {
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuTrigger";
import {
  NavigationMenuViewport,
} from "@/ui/components/navigation/NavigationMenu/NavigationMenuViewport";
import { cn } from "@/ui/lib/utils";

export const NavigationMenu = forwardRef<
  TNavigationMenuElement,
  TNavigationMenuProps
>(({ className, items, listProps, viewport = true, ...props }, ref) => (
  <NavigationMenuPrimitive.Root
    ref={ref}
    data-component="NavigationMenu"
    className={cn(
      "group/navigation-menu relative flex max-w-max flex-1 items-center justify-center",
      className,
    )}
    {...props}
  >
    <NavigationMenuList {...listProps}>
      { items.map((item) => (
        "type" in item ? (
          <NavigationMenuSeparator
            key={item.id}
            {...item.itemProps}
            orientation={item.orientation ?? "vertical"}
            className={item.className}
          />
        ) : (
          <NavigationMenuItem key={item.label} {...item.itemProps}>
            { "links" in item ? (
              <>
                <NavigationMenuTrigger {...item.triggerProps}>
                  { item.label }
                </NavigationMenuTrigger>
                <NavigationMenuContent {...item.contentProps}>
                  <ul className={NAVIGATION_MENU_CONTENT_LIST_CLASS}>
                    { item.links.map((contentItem) => (
                      "type" in contentItem ? (
                        <NavigationMenuSeparator
                          key={contentItem.id}
                          {...contentItem.itemProps}
                          orientation={contentItem.orientation}
                          className={contentItem.className}
                        />
                      ) : (
                        <li key={contentItem.href}>
                          <NavigationMenuLink
                            {...contentItem.linkProps}
                            href={contentItem.href}
                            icon={contentItem.icon}
                            className={contentItem.className}
                          >
                            { contentItem.label }
                          </NavigationMenuLink>
                        </li>
                      )
                    )) }
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink
                {...item.linkProps}
                href={item.href}
                icon={item.icon}
                className={cn(navigationMenuTriggerStyle(), item.className)}
              >
                { item.label }
              </NavigationMenuLink>
            ) }
          </NavigationMenuItem>
        )
      )) }
    </NavigationMenuList>
    { viewport && <NavigationMenuViewport /> }
  </NavigationMenuPrimitive.Root>
));

NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;
